/**
 * User Profile Hook
 *
 * Fetches the current user's profile including their role.
 *
 * @fileoverview Custom hook for fetching user profile with role
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { apiJson } from '../lib/api';
import { toUserMessage } from '../lib/apiError';

/**
 * User profile interface from backend
 */
export interface UserProfile {
  id: string;
  email: string;
  globalRole: 'ADMIN' | 'USER';
  isActive: boolean;
  displayName?: string;
  avatarUrl?: string;
}

/**
 * Hook for fetching the current user's profile
 *
 * @returns User profile state and methods
 */
export function useUserProfile() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        setProfile(null);
        if (!silent) setLoading(false);
        return;
      }

      const data: UserProfile = await apiJson<UserProfile>(
        '/api/auth/me',
        {},
        session.access_token
      );
      setProfile(data);
    } catch (err) {
      if (!silent) {
        setError(toUserMessage(err, "We couldn't load your profile. Please try again."));
        setProfile(null);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();

    // Listen for auth state changes. Token refreshes are skipped entirely;
    // all other events (SIGNED_IN, SIGNED_OUT, USER_UPDATED…) silently
    // refresh the profile without triggering the loading spinner.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') return;
      fetchProfile(true);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const isAdmin = profile?.globalRole === 'ADMIN';

  return {
    profile,
    loading,
    error,
    isAdmin,
    refetch: fetchProfile,
  };
}

export default useUserProfile;
